/*
 * Round scoring. Pure and shared in spirit with backend/utils/starguessrScoring.js,
 * which recomputes all of this server-side — the client's numbers are for instant
 * feedback only and are never trusted by the leaderboard.
 *
 * Both tiers ask the same question — where on Earth are you — and are scored
 * identically. They differ only in how much help the sky viewer gives while you
 * answer: Easy names the constellations and bright stars and marks the cardinal
 * points from the start, Hard shows none of that until the guess is locked in.
 *
 * An earlier Historic tier also asked you to date the sky from the Moon's phase.
 * It was dropped: solvable in principle, but it demanded that a player know
 * where the Sun sits among the constellations on an arbitrary date, which is a
 * different and far more specialised skill than the navigation the rest of the
 * game teaches.
 */

export const MAX_ROUND_SCORE = 10000;
export const ROUNDS_PER_GAME = 3;

/*
 * The e-folding length of a plain exponential, solved from one judgement about
 * play: a 300 km miss reads as dead on the pin at any zoom you can actually see,
 * and should score about 9,600. Everything else follows from that.
 *
 * The curve is deliberately forgiving as a result — 10,000 km out still keeps a
 * quarter of the round. An earlier pass held a second anchor as well (3,350 km
 * scoring 3,000), which no plain exponential can satisfy alongside this one and
 * which cost an exponent to hold. Rewarding a near miss properly was worth more
 * than punishing a far one.
 */
const LOCATION_DECAY_KM = 7350;

const EARTH_RADIUS_KM = 6371;
const DEG = Math.PI / 180;

export function haversineKm(lat1, lon1, lat2, lon2) {
  const dLat = (lat2 - lat1) * DEG;
  const dLon = (lon2 - lon1) * DEG;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function scoreForDistance(distanceKm) {
  return MAX_ROUND_SCORE * Math.exp(-distanceKm / LOCATION_DECAY_KM);
}

export const TIERS = {
  easy: { key: 'easy', label: 'Easy', assisted: true },
  hard: { key: 'hard', label: 'Hard', assisted: false }
};

export function isTier(value) {
  return Object.prototype.hasOwnProperty.call(TIERS, value);
}

/*
 * Distance is the whole of it. The tiers are scored the same way on purpose —
 * the difference between them is the help on screen, not the question — but the
 * leaderboard is still split by tier, because the same distance is a great deal
 * harder to achieve without the labels.
 */
export function scoreRound({ guessLat, guessLon, actualLat, actualLon }) {
  const distanceKm = haversineKm(guessLat, guessLon, actualLat, actualLon);
  const locationScore = Math.round(scoreForDistance(distanceKm));
  return { distanceKm, locationScore, roundScore: locationScore };
}
